import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Mail,
  Check,
  X,
  Minus,
  Palette,
  Globe,
  Share2,
  Loader2,
  Trophy,
  AlertTriangle,
  ShieldCheck,
  Clock,
  FileText,
  TrendingUp,
  ChevronDown,
  Star,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TITLE =
  "Diagnostic Digital Gratuit — EM Group | Score en 2 min";
const DESCRIPTION =
  "Combien de clients perdez-vous chaque mois ? Diagnostic 360° gratuit : logo, site web, réseaux sociaux. Score sur 100 + rapport PDF personnalisé. 2 minutes, sans engagement.";
const OG_TITLE = "Diagnostic Digital Gratuit — EM Group";
const OG_DESCRIPTION = "Découvrez votre score digital en 2 minutes. Rapport PDF offert.";

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const ANSWER_OPTIONS = [
  { value: "yes", label: "Oui", Icon: Check, color: "#1B8A4F" },
  { value: "partial", label: "Partiellement", Icon: Minus, color: "#C46B00" },
  { value: "no", label: "Non / je ne sais pas", Icon: X, color: "#E52431" },
];

const SECTIONS = [
  {
    id: "logo",
    icon: Palette,
    title: "Logo & identité visuelle",
    questions: [
      { key: "logo_pro", label: "Avez-vous un logo professionnel ?" },
      {
        key: "logo_coherent",
        label: "Votre logo est-il cohérent avec votre activité ?",
      },
      { key: "charte_graphique", label: "Avez-vous une charte graphique ?" },
      {
        key: "typo_definie",
        label: "Vos couleurs et typographies sont-elles définies ?",
      },
    ],
  },
  {
    id: "site",
    icon: Globe,
    title: "Site web",
    questions: [
      { key: "site_existe", label: "Avez-vous un site web ?" },
      { key: "site_rapide", label: "Votre site est-il rapide ?" },
      { key: "site_responsive", label: "Votre site est-il responsive mobile ?" },
      {
        key: "site_cta",
        label: "Votre site possède-t-il des appels à l'action clairs ?",
      },
      {
        key: "site_demandes",
        label: "Recevez-vous des demandes via votre site ?",
      },
    ],
  },
  {
    id: "rs",
    icon: Share2,
    title: "Réseaux sociaux",
    questions: [
      { key: "rs_actif", label: "Êtes-vous actif sur les réseaux ?" },
      { key: "rs_visuels", label: "Vos visuels sont-ils cohérents ?" },
      { key: "rs_regulier", label: "Publiez-vous régulièrement ?" },
      {
        key: "rs_contacts",
        label: "Vos réseaux génèrent-ils des contacts ?",
      },
      { key: "rs_strategie", label: "Avez-vous une stratégie de contenu ?" },
    ],
  },
];

const ALL_QUESTION_KEYS = SECTIONS.flatMap((s) => s.questions.map((q) => q.key));

const initialProspect = {
  first_name: "",
  email: "",
  phone: "",
  company: "",
  website: "",
  activity: "",
  goal: "",
  instagram: "",
  facebook: "",
  tiktok: "",
  linkedin: "",
};

// --- Conversion tracking (Google Ads + Meta Pixel) -------------------------
const GOOGLE_ADS_CONVERSION_ID = "AW-18201979242";
const GOOGLE_ADS_LEAD_LABEL = "kUtFCJzRrrccEOrSsOdD"; // action "Envoi de formulaire de lead"
const META_PIXEL_ID = null;

function fireLeadConversion(value = 5) {
  // Google Ads — generate_lead
  if (
    GOOGLE_ADS_CONVERSION_ID &&
    GOOGLE_ADS_LEAD_LABEL &&
    typeof window !== "undefined" &&
    window.gtag
  ) {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`,
      value,
      currency: "EUR",
    });
  }
  // Meta Pixel — Lead
  if (META_PIXEL_ID && typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Lead", {
      content_name: "Diagnostic digital gratuit",
      value,
      currency: "EUR",
    });
  }
}

export default function DiagnosticDigital() {
  const [step, setStep] = useState(0); // 0..4
  const [prospect, setProspect] = useState(initialProspect);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    setMeta("description", DESCRIPTION);
    setMeta("og:title", OG_TITLE, "property");
    setMeta("og:description", OG_DESCRIPTION, "property");
    setMeta("og:type", "website", "property");
    return () => {
      document.title = prev;
    };
  }, []);

  const totalQuestions = ALL_QUESTION_KEYS.length;
  const answeredCount = useMemo(
    () => ALL_QUESTION_KEYS.filter((k) => answers[k]).length,
    [answers]
  );

  const handleStart = () => {
    // Fire Google Ads "lead form started" conversion on CTA click
    if (typeof window !== "undefined" && typeof window.gtag_report_conversion === "function") {
      console.log("conversion fired");
      window.gtag_report_conversion();
    }
    setStep(1);
  };

  const validateStep1 = () => {
    if (!prospect.first_name.trim()) return "Merci d'indiquer votre prénom.";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(prospect.email)) return "Email invalide.";
    if (!prospect.website.trim())
      return "Indiquez votre site web (ou tapez « aucun » si vous n'en avez pas).";
    return null;
  };

  const goNext = () => {
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        toast.error(err);
        return;
      }
    }
    if (step >= 2 && step <= 4) {
      const section = SECTIONS[step - 2];
      const missing = section.questions.find((q) => !answers[q.key]);
      if (missing) {
        toast.error("Merci de répondre à toutes les questions de l'étape.");
        return;
      }
    }
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        prospect: {
          ...prospect,
          phone: prospect.phone || null,
          company: prospect.company || null,
          website: prospect.website || null,
          activity: prospect.activity || null,
          goal: prospect.goal || null,
          instagram: prospect.instagram || null,
          facebook: prospect.facebook || null,
          tiktok: prospect.tiktok || null,
          linkedin: prospect.linkedin || null,
        },
        answers,
      };
      const res = await axios.post(`${API}/diagnostic`, payload);
      setResult(res.data);
      setStep(5);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Fire conversion AFTER successful submission only
      fireLeadConversion(5);
    } catch (e) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleBuyAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/audit`, {
        origin_url: window.location.origin,
        email: prospect.email,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Erreur de redirection vers le paiement.");
    } catch {
      toast.error("Impossible de démarrer le paiement. Merci de réessayer.");
    } finally {
      setAuditLoading(false);
    }
  };

  const progress = step === 0 ? 0 : step === 5 ? 100 : (step / 5) * 100;

  return (
    <div
      className="min-h-screen bg-[#050507] text-white"
      data-testid="page-diagnostic"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home-logo">
            <img
              src="/brand/logo-em-group.png"
              alt="EM Group"
              className="h-10 w-10 object-contain"
              style={{ maxWidth: 40, maxHeight: 40 }}
            />
            <span className="font-outfit tracking-tight text-lg">
              EM <span className="text-white/50">Group</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Progress bar (steps 1-4) */}
      {step >= 1 && step <= 4 && (
        <div className="sticky top-[73px] z-30 bg-[#050507]/95 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-4">
            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress}%`,
                  background:
                    "linear-gradient(90deg, #E52431 0%, #1F2C6E 100%)",
                }}
              />
            </div>
            <div className="text-xs text-white/60 font-light uppercase tracking-wider">
              Étape {step} / 4
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <Hero key="hero" onStart={handleStart} />
          )}

          {step === 1 && (
            <ProspectStep
              key="step1"
              prospect={prospect}
              setProspect={setProspect}
              onNext={goNext}
              onPrev={goPrev}
            />
          )}

          {step === 2 && (
            <QuestionStep
              key="step2"
              section={SECTIONS[0]}
              answers={answers}
              setAnswers={setAnswers}
              onNext={goNext}
              onPrev={goPrev}
            />
          )}
          {step === 3 && (
            <QuestionStep
              key="step3"
              section={SECTIONS[1]}
              answers={answers}
              setAnswers={setAnswers}
              onNext={goNext}
              onPrev={goPrev}
            />
          )}
          {step === 4 && (
            <QuestionStep
              key="step4"
              section={SECTIONS[2]}
              answers={answers}
              setAnswers={setAnswers}
              onNext={handleSubmit}
              onPrev={goPrev}
              isLast
              loading={loading}
              progressLabel={`${answeredCount}/${totalQuestions} questions`}
            />
          )}

          {step === 5 && result && (
            <ResultStep
              key="result"
              result={result}
              prospect={prospect}
              onBuyAudit={handleBuyAudit}
              auditLoading={auditLoading}
            />
          )}
        </AnimatePresence>
      </main>

      {/* Sticky CTA mobile — only on Hero step */}
      {step === 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 sm:hidden p-3 bg-gradient-to-t from-[#050507] via-[#050507]/95 to-transparent pointer-events-none"
          data-testid="sticky-cta-mobile"
        >
          <button
            onClick={handleStart}
            className="pointer-events-auto w-full inline-flex items-center justify-center gap-3 rounded-full gradient-bg text-white font-semibold px-7 py-4 text-sm uppercase tracking-[0.15em] shadow-2xl shadow-[#E52431]/30 btn-shine"
          >
            Démarrer mon diagnostic
            <ArrowRight size={16} />
          </button>
        </div>
      )}

      <footer className="py-10 bg-[#0A0A0A] border-t border-white/5 mt-10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div>
            © {new Date().getFullYear()} EM Group — Agence digitale premium.
          </div>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-white/80">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-white/80">
              Confidentialité
            </Link>
            <Link to="/cgv" className="hover:text-white/80">
              CGV
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ----------------------- Hero ----------------------- */
function Hero({ onStart }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      data-testid="diagnostic-hero"
    >
      {/* ============ Above-the-fold ============ */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-7">
          <Sparkles size={14} className="text-[#E52431]" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-white/80 font-medium">
            Diagnostic premium gratuit
          </span>
        </div>

        <h1 className="font-outfit text-[34px] leading-[1.08] sm:text-5xl lg:text-6xl font-light tracking-tight">
          Combien de clients <span className="gradient-text font-medium">perdez-vous chaque mois</span> à cause de votre image digitale ?
        </h1>

        <p className="mt-6 text-white/75 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto">
          Diagnostic 360° de votre <strong className="text-white font-normal">logo</strong>, votre <strong className="text-white font-normal">site web</strong> et vos <strong className="text-white font-normal">réseaux sociaux</strong>. En 2 minutes, identifiez exactement ce qui fait fuir vos clients potentiels.
        </p>

        <button
          onClick={onStart}
          data-testid="diagnostic-start"
          className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full gradient-bg text-white font-semibold px-9 py-5 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
        >
          Démarrer mon diagnostic gratuit
          <ArrowRight size={16} />
        </button>

        <p className="mt-4 text-xs text-white/55 font-light">
          14 questions · 2 minutes · Sans engagement
        </p>

        {/* Trust badges row */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] text-white/60 font-light">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-[#3FB874]" /> 100% Gratuit
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-[#3FB874]" /> Données RGPD
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock size={13} className="text-[#3FB874]" /> PDF sous 2 min
          </span>
        </div>

        {/* Social proof block */}
        <div
          className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full border border-white/10 bg-white/[0.03]"
          data-testid="social-proof"
        >
          <div className="flex items-center -space-x-0.5 text-[#FFC940]">
            <Star size={13} fill="currentColor" strokeWidth={0} />
            <Star size={13} fill="currentColor" strokeWidth={0} />
            <Star size={13} fill="currentColor" strokeWidth={0} />
            <Star size={13} fill="currentColor" strokeWidth={0} />
            <Star size={13} fill="currentColor" strokeWidth={0} />
          </div>
          <span className="text-xs text-white/75 font-light">
            Déjà <span className="text-white font-semibold">230+ dirigeants</span> ont découvert leur score digital
          </span>
        </div>
      </div>

      {/* ============ "Ce que vous recevrez" — PDF mockup ============ */}
      <div className="mt-20" data-testid="diagnostic-pdf-preview">
        <div className="text-center mb-10">
          <span className="text-[11px] tracking-[2px] text-white/40 font-semibold">
            — CE QUE VOUS RECEVREZ
          </span>
          <h2 className="mt-3 font-outfit text-2xl sm:text-3xl font-light tracking-tight">
            Votre <span className="gradient-text font-medium">score sur 100</span>. Vos 3 actions prioritaires. Lisible en 3 minutes.
          </h2>
        </div>

        <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 items-center max-w-4xl mx-auto">
          {/* Stylized PDF mockup */}
          <div className="relative">
            <div className="absolute inset-0 -m-4 rounded-3xl bg-gradient-to-br from-[#E52431]/15 via-transparent to-[#1F2C6E]/20 blur-2xl" />
            <div className="relative rounded-2xl border border-white/10 bg-[#0A0A0A] p-5 sm:p-6 shadow-2xl shadow-black/50">
              {/* Mock PDF page */}
              <div className="rounded-xl bg-[#0F0F14] border border-white/5 p-5 aspect-[1/1.25] flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded bg-gradient-to-br from-[#E52431] to-[#1F2C6E]" />
                  <span className="text-[10px] tracking-[2px] text-white/50 font-semibold">EM GROUP</span>
                </div>
                <div className="text-[9px] tracking-[2px] text-white/40 font-semibold">— RAPPORT</div>
                <div className="mt-2 text-xl sm:text-2xl font-bold text-white leading-tight">
                  Diagnostic <span className="text-[#E52431]">Digital</span>
                </div>
                {/* Score circle */}
                <div className="mt-5 flex items-center gap-4">
                  <div className="relative shrink-0">
                    <div className="w-20 h-20 rounded-full border-2 border-[#E52431] flex items-center justify-center bg-[#E52431]/10">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white leading-none">68</div>
                        <div className="text-[8px] text-white/60 mt-0.5">/100</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 flex-1">
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-white/60 w-14">Logo</span>
                      <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#E52431] to-[#1F2C6E]" style={{ width: "70%" }} />
                      </div>
                      <span className="text-white text-[10px] w-7 text-right">70</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-white/60 w-14">Site</span>
                      <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#E52431] to-[#1F2C6E]" style={{ width: "80%" }} />
                      </div>
                      <span className="text-white text-[10px] w-7 text-right">80</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-white/60 w-14">Réseaux</span>
                      <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#E52431] to-[#1F2C6E]" style={{ width: "55%" }} />
                      </div>
                      <span className="text-white text-[10px] w-7 text-right">55</span>
                    </div>
                  </div>
                </div>
                <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                  <div className="text-[8px] text-white/40">emgroupdigital.fr</div>
                  <div className="text-[8px] text-white/40">1 / 5</div>
                </div>
              </div>
            </div>
            {/* "Aperçu" pill */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#E52431] text-white text-[10px] tracking-[2px] font-semibold shadow-lg shadow-[#E52431]/40">
              APERÇU DU RAPPORT
            </div>
          </div>

          {/* Bullets */}
          <ul className="space-y-4">
            {[
              { Icon: TrendingUp, title: "Votre score sur 100", desc: "Synthèse de votre présence digitale en un coup d'œil." },
              { Icon: Palette, title: "Analyse des 3 axes clés", desc: "Logo, site web, réseaux sociaux — évalués question par question." },
              { Icon: FileText, title: "Recommandations dynamiques", desc: "Les 3 actions prioritaires en fonction de vos résultats." },
              { Icon: Sparkles, title: "Plan d'action concret", desc: "Étapes claires pour transformer votre image digitale." },
            ].map((it) => (
              <li key={it.title} className="flex gap-4">
                <div className="shrink-0 w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <it.Icon size={16} className="text-[#E52431]" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-medium text-white text-sm">{it.title}</div>
                  <div className="text-white/55 text-xs mt-1 leading-relaxed">{it.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ============ Secondary CTA + reassurance ============ */}
      <div className="mt-20 text-center max-w-2xl mx-auto" data-testid="diagnostic-secondary-cta">
        <h3 className="font-outfit text-2xl font-light">
          Votre image digitale vaut mieux que <span className="gradient-text font-medium">l'improvisation</span>.
        </h3>
        <p className="mt-3 text-white/60 text-sm">
          2 minutes pour le prouver.
        </p>
        <button
          onClick={onStart}
          data-testid="diagnostic-start-secondary"
          className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full gradient-bg text-white font-semibold px-9 py-5 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
        >
          Je veux connaître mon score
          <ArrowRight size={16} />
        </button>
      </div>
    </motion.section>
  );
}

/* ----------------------- Step 1 : Prospect ----------------------- */
function Field({ label, name, prospect, setProspect, type = "text", required, placeholder }) {
  return (
    <label className="block" data-testid={`field-${name}`}>
      <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-medium">
        {label} {required && <span className="text-[#E52431]">*</span>}
      </span>
      <input
        type={type}
        placeholder={placeholder}
        value={prospect[name] || ""}
        onChange={(e) =>
          setProspect((p) => ({ ...p, [name]: e.target.value }))
        }
        className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 focus:bg-white/8 outline-none transition-all"
      />
    </label>
  );
}

function ProspectStep({ prospect, setProspect, onNext, onPrev }) {
  const [showOptional, setShowOptional] = useState(false);
  const [showSocial, setShowSocial] = useState(false);

  return (
    <motion.section
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      data-testid="diagnostic-step-prospect"
    >
      <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
        — Étape 1
      </span>
      <h2 className="mt-3 font-outfit text-3xl md:text-4xl font-light tracking-tight">
        Quelques infos pour personnaliser{" "}
        <span className="gradient-text font-medium">votre diagnostic</span>
      </h2>
      <p className="mt-4 text-white/60 font-light">
        3 champs obligatoires. Vos données restent confidentielles et ne servent
        qu'à vous envoyer votre rapport PDF.
      </p>

      {/* --- Required fields --- */}
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        <Field
          label="Prénom"
          name="first_name"
          prospect={prospect}
          setProspect={setProspect}
          required
          placeholder="Jean"
        />
        <Field
          label="Email"
          name="email"
          prospect={prospect}
          setProspect={setProspect}
          type="email"
          required
          placeholder="jean@entreprise.fr"
        />
        <div className="md:col-span-2">
          <Field
            label="Site web"
            name="website"
            prospect={prospect}
            setProspect={setProspect}
            required
            placeholder="https://… (ou « aucun » si vous n'en avez pas encore)"
          />
        </div>
      </div>

      {/* --- Optional, collapsible --- */}
      <div className="mt-6 space-y-3">
        <Collapsible
          open={showOptional}
          onToggle={() => setShowOptional((v) => !v)}
          label="Affiner mon diagnostic"
          badge="Recommandé · +30 sec"
          testId="toggle-optional"
        >
          <div className="grid md:grid-cols-2 gap-5 pt-2">
            <Field
              label="Activité de votre entreprise"
              name="activity"
              prospect={prospect}
              setProspect={setProspect}
              placeholder="Ex : restaurant, coach, e-commerce…"
            />
            <Field
              label="Nom de l'entreprise"
              name="company"
              prospect={prospect}
              setProspect={setProspect}
            />
            <div className="md:col-span-2">
              <Field
                label="Objectif principal des prochains mois"
                name="goal"
                prospect={prospect}
                setProspect={setProspect}
                placeholder="Ex : générer plus de clients, refondre mon image…"
              />
            </div>
            <Field
              label="Téléphone"
              name="phone"
              prospect={prospect}
              setProspect={setProspect}
              type="tel"
            />
          </div>
        </Collapsible>

        <Collapsible
          open={showSocial}
          onToggle={() => setShowSocial((v) => !v)}
          label="Vos réseaux sociaux"
          badge="Facultatif"
          testId="toggle-social"
        >
          <div className="grid md:grid-cols-2 gap-5 pt-2">
            <Field label="Instagram" name="instagram" prospect={prospect} setProspect={setProspect} placeholder="@compte" />
            <Field label="Facebook" name="facebook" prospect={prospect} setProspect={setProspect} />
            <Field label="TikTok" name="tiktok" prospect={prospect} setProspect={setProspect} placeholder="@compte" />
            <Field label="LinkedIn" name="linkedin" prospect={prospect} setProspect={setProspect} />
          </div>
        </Collapsible>
      </div>

      <StepNav onPrev={onPrev} onNext={onNext} nextLabel="Commencer le diagnostic" />
    </motion.section>
  );
}

function Collapsible({ open, onToggle, label, badge, testId, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        data-testid={testId}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.03] transition-colors"
      >
        <span className="flex items-center gap-3">
          <span className="text-sm font-medium text-white">{label}</span>
          {badge && (
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`text-white/50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ----------------------- Question Step ----------------------- */
function QuestionStep({
  section,
  answers,
  setAnswers,
  onNext,
  onPrev,
  isLast,
  loading,
  progressLabel,
}) {
  const Icon = section.icon;
  return (
    <motion.section
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      data-testid={`diagnostic-step-${section.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#E52431]/20 to-[#1F2C6E]/30 border border-white/10 grid place-items-center">
          <Icon size={20} className="text-white" strokeWidth={1.5} />
        </div>
        <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
          — {section.title}
        </span>
      </div>
      <h2 className="mt-5 font-outfit text-3xl md:text-4xl font-light tracking-tight">
        Votre{" "}
        <span className="gradient-text font-medium">{section.title.toLowerCase()}</span>
      </h2>

      <div className="mt-10 space-y-5">
        {section.questions.map((q, idx) => (
          <div
            key={q.key}
            className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 md:p-6"
            data-testid={`question-${q.key}`}
          >
            <p className="text-white font-light text-base md:text-lg">
              <span className="text-[#E52431] font-medium">{idx + 1}.</span> {q.label}
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {ANSWER_OPTIONS.map((opt) => {
                const I = opt.Icon;
                const active = answers[q.key] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() =>
                      setAnswers((a) => ({ ...a, [q.key]: opt.value }))
                    }
                    data-testid={`answer-${q.key}-${opt.value}`}
                    className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 border ${
                      active
                        ? "border-white/40 bg-white/10 text-white scale-[1.02]"
                        : "border-white/10 bg-white/[0.02] text-white/65 hover:border-white/20 hover:text-white"
                    }`}
                  >
                    <I
                      size={15}
                      style={{ color: active ? opt.color : undefined }}
                    />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <StepNav
        onPrev={onPrev}
        onNext={onNext}
        nextLabel={isLast ? "Voir mon score" : "Continuer"}
        loading={loading}
        progressLabel={progressLabel}
      />
    </motion.section>
  );
}

function StepNav({ onPrev, onNext, nextLabel, loading, progressLabel }) {
  return (
    <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
      <button
        onClick={onPrev}
        data-testid="step-prev"
        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors px-4 py-2"
      >
        <ArrowLeft size={14} /> Retour
      </button>

      {progressLabel && (
        <div className="text-xs text-white/50 font-light uppercase tracking-wider">
          {progressLabel}
        </div>
      )}

      <button
        onClick={onNext}
        disabled={loading}
        data-testid="step-next"
        className="inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_40px_rgba(229,36,49,0.4)] transition-all duration-300 btn-shine disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Envoi…
          </>
        ) : (
          <>
            {nextLabel}
            <ArrowRight size={14} />
          </>
        )}
      </button>
    </div>
  );
}

/* ----------------------- Result ----------------------- */
function ResultStep({ result, prospect, onBuyAudit, auditLoading }) {
  const { scores, strengths, weaknesses } = result;
  const tone =
    scores.total >= 70 ? "high" : scores.total >= 40 ? "medium" : "low";
  const toneColor =
    tone === "high" ? "#1B8A4F" : tone === "medium" ? "#C46B00" : "#E52431";

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.5 }}
      data-testid="diagnostic-result"
    >
      <div className="text-center">
        <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
          — Votre résultat
        </span>
        <h2 className="mt-3 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
          Votre <span className="gradient-text font-medium">score digital</span>
        </h2>
      </div>

      {/* Big score */}
      <div
        className="mt-10 mx-auto max-w-md rounded-3xl p-10 text-center border"
        style={{
          background:
            "linear-gradient(135deg, #160812 0%, #0B1030 100%)",
          borderColor: `${toneColor}55`,
          boxShadow: `0 30px 80px -20px ${toneColor}44`,
        }}
        data-testid="result-score-card"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-white/60">
          Votre score digital
        </p>
        <div
          className="mt-4 font-outfit text-7xl md:text-8xl font-light gradient-text leading-none"
          data-testid="result-total-score"
        >
          {scores.total}
        </div>
        <p className="mt-2 text-white/60 font-light">/ 100</p>
      </div>

      {/* Per-axis scores */}
      <div className="mt-10 grid md:grid-cols-3 gap-4">
        {[
          { label: "Logo / Identité", value: scores.logo, Icon: Palette },
          { label: "Site web", value: scores.site, Icon: Globe },
          { label: "Réseaux sociaux", value: scores.rs, Icon: Share2 },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-2xl bg-white/[0.04] border border-white/10 p-5"
          >
            <div className="flex items-center gap-3">
              <s.Icon size={18} className="text-[#E52431]" strokeWidth={1.5} />
              <span className="text-white/85 font-light text-sm">
                {s.label}
              </span>
            </div>
            <div className="mt-3 font-outfit text-3xl font-light text-white">
              {s.value}
              <span className="text-white/40 text-base font-light"> / 100</span>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${s.value}%`,
                  background:
                    "linear-gradient(90deg, #E52431 0%, #1F2C6E 100%)",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Strengths / weaknesses */}
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        <div
          className="rounded-2xl bg-gradient-to-b from-[#1B8A4F]/10 to-transparent border border-[#1B8A4F]/25 p-6"
          data-testid="result-strengths"
        >
          <div className="flex items-center gap-2 text-[#3FB874]">
            <Trophy size={16} />
            <span className="text-xs uppercase tracking-wider font-semibold">
              Vos points forts
            </span>
          </div>
          <ul className="mt-4 space-y-2.5">
            {strengths.length ? (
              strengths.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-3 text-white/80 font-light text-sm"
                >
                  <Check size={14} className="text-[#3FB874] mt-1 flex-shrink-0" />
                  {s}
                </li>
              ))
            ) : (
              <li className="text-white/55 font-light text-sm italic">
                Aucun point fort marqué — c'est le bon moment pour agir.
              </li>
            )}
          </ul>
        </div>

        <div
          className="rounded-2xl bg-gradient-to-b from-[#E52431]/10 to-transparent border border-[#E52431]/25 p-6"
          data-testid="result-weaknesses"
        >
          <div className="flex items-center gap-2 text-[#FF6B7A]">
            <AlertTriangle size={16} />
            <span className="text-xs uppercase tracking-wider font-semibold">
              Points à améliorer
            </span>
          </div>
          <ul className="mt-4 space-y-2.5">
            {weaknesses.length ? (
              weaknesses.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-3 text-white/80 font-light text-sm"
                >
                  <X size={14} className="text-[#FF6B7A] mt-1 flex-shrink-0" />
                  {w}
                </li>
              ))
            ) : (
              <li className="text-white/55 font-light text-sm italic">
                Excellent — peu d'axes critiques identifiés.
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Email confirmation */}
      <div className="mt-10 rounded-2xl bg-white/[0.04] border border-white/10 p-5 md:p-6 flex items-start gap-4">
        <Mail size={20} className="text-[#E52431] mt-1 flex-shrink-0" />
        <div>
          <p className="text-white font-medium text-sm md:text-base">
            Votre mini-audit PDF est en route
          </p>
          <p className="mt-1 text-white/60 font-light text-sm">
            Nous venons d'envoyer votre diagnostic complet à{" "}
            <strong className="text-white font-normal">{prospect.email}</strong>.
            Pensez à vérifier votre dossier spam.
          </p>
        </div>
      </div>

      {/* CTA Audit complet */}
      <div
        className="mt-10 rounded-3xl p-8 md:p-10 overflow-hidden relative"
        data-testid="result-cta-audit"
        style={{
          background:
            "linear-gradient(135deg, #0A0A0A 0%, #160812 35%, #0B1030 75%, #0A0A0A 100%)",
        }}
      >
        <div className="absolute -top-20 -left-10 w-[300px] h-[300px] rounded-full bg-[#E52431]/25 blur-[80px]" />
        <div className="absolute -bottom-20 -right-10 w-[300px] h-[300px] rounded-full bg-[#1F2C6E]/25 blur-[80px]" />

        <div className="relative z-10">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-5">
            <Sparkles size={11} /> Aller plus loin
          </span>
          <h3 className="font-outfit text-2xl md:text-3xl font-light text-white leading-tight">
            Audit complet EM Group —{" "}
            <span className="gradient-text font-medium">190 €</span>
            <span className="text-[11px] font-semibold tracking-[0.15em] text-white/60 ml-1.5 align-middle">TTC</span>
          </h3>
          <p className="mt-4 text-white/70 font-light leading-relaxed max-w-2xl">
            Pour aller plus loin, EM Group réalise un audit complet avec
            analyse détaillée, recommandations concrètes et plan d'action
            personnalisé. Rapport livré sous 3 à 5 jours. <strong className="text-white font-normal">100% remboursé</strong> si vous nous confiez la refonte.
          </p>
          <button
            onClick={onBuyAudit}
            disabled={auditLoading}
            data-testid="result-buy-audit"
            className="mt-7 inline-flex items-center gap-3 rounded-full bg-white text-[#0A0A0A] font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(255,255,255,0.25)] transition-all duration-300 btn-shine disabled:opacity-60"
          >
            {auditLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Redirection…
              </>
            ) : (
              <>
                Commander mon audit complet
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/"
          data-testid="result-back-home"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> Retour à l'accueil
        </Link>
      </div>
    </motion.section>
  );
}
